import { useState, Fragment } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Info, ChevronDown, ChevronRight, Search, X, Filter } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const PlanningFinishedGoodsPage = () => {
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedStockStatus, setSelectedStockStatus] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);

  const toggleRow = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const openDetailModal = (product) => {
    setSelectedProduct(product);
    setShowDetailModal(true);
  };

  // Calculate weeks on hand based on inventory and average weekly shipments
  const calculateWeeksOnHand = (inventory, avgWeeklyShipments) => {
    if (avgWeeklyShipments === 0) return 999;
    return Math.max(0, inventory / avgWeeklyShipments);
  };

  // Generate week headers as "Week Of" dates (Monday of each week)
  const getWeekHeaders = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    
    // Calculate the Monday of the current week
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const currentMonday = new Date(today);
    currentMonday.setDate(today.getDate() - daysToSubtract);
    
    // Generate 13 week headers
    const weeks = [];
    for (let i = 0; i < 13; i++) {
      const weekDate = new Date(currentMonday);
      weekDate.setDate(currentMonday.getDate() + (i * 7));
      const month = weekDate.getMonth() + 1;
      const day = weekDate.getDate();
      weeks.push(`${month}/${day}`);
    }
    return weeks;
  };

  const weekHeaders = getWeekHeaders();

  // Mock data for finished goods MRP
  // Formula: Ending Balance = Beginning + Planned Production (WOs) - Planned Shipments (SOs from NetSuite)
  const finishedGoodsData = [
    {
      id: 1,
      sku: "EVOL-281",
      name: "Egg and Green Chili Burrito Club Pack",
      category: "Breakfast Burritos",
      avgWeeklyShipments: 580,
      beginningInventory: 2400,
      weeks: [
        { production: 500, productionDetails: [{ ref: "WO-00101", qty: 500, line: "P1" }], shipments: 600, shipmentDetails: [{ ref: "SO-50234", qty: 400, customer: "Costco" }, { ref: "SO-50235", qty: 200, customer: "Target" }], ending: 2300 },
        { production: 500, productionDetails: [{ ref: "WO-00102", qty: 500, line: "P1" }], shipments: 550, shipmentDetails: [{ ref: "SO-50236", qty: 350, customer: "Costco" }, { ref: "SO-50237", qty: 200, customer: "Walmart" }], ending: 2250 },
        { production: 500, productionDetails: [{ ref: "WO-00103", qty: 500, line: "P2" }], shipments: 620, shipmentDetails: [{ ref: "SO-50238", qty: 420, customer: "Costco" }, { ref: "SO-50239", qty: 200, customer: "Target" }], ending: 2130, hasIntraWeekNegative: true },
        { production: 0, productionDetails: [], shipments: 580, shipmentDetails: [{ ref: "SO-50240", qty: 380, customer: "Costco" }, { ref: "SO-50241", qty: 200, customer: "Safeway" }], ending: 1550 },
        { production: 500, productionDetails: [{ ref: "WO-00104", qty: 500, line: "P1" }], shipments: 600, shipmentDetails: [{ ref: "SO-50242", qty: 400, customer: "Costco" }, { ref: "SO-50243", qty: 200, customer: "Target" }], ending: 1450 },
        { production: 500, productionDetails: [{ ref: "WO-00105", qty: 500, line: "P1" }], shipments: 550, shipmentDetails: [{ ref: "SO-50244", qty: 350, customer: "Costco" }, { ref: "SO-50245", qty: 200, customer: "Walmart" }], ending: 1400 },
        { production: 500, productionDetails: [{ ref: "WO-00106", qty: 500, line: "P2" }], shipments: 620, shipmentDetails: [{ ref: "SO-50246", qty: 420, customer: "Costco" }, { ref: "SO-50247", qty: 200, customer: "Target" }], ending: 1280 },
        { production: 0, productionDetails: [], shipments: 580, shipmentDetails: [{ ref: "SO-50248", qty: 380, customer: "Costco" }, { ref: "SO-50249", qty: 200, customer: "Safeway" }], ending: 700 },
        { production: 800, productionDetails: [{ ref: "WO-00107", qty: 800, line: "P1" }], shipments: 600, shipmentDetails: [{ ref: "SO-50250", qty: 400, customer: "Costco" }, { ref: "SO-50251", qty: 200, customer: "Target" }], ending: 900 },
        { production: 0, productionDetails: [], shipments: 550, shipmentDetails: [{ ref: "SO-50252", qty: 350, customer: "Costco" }, { ref: "SO-50253", qty: 200, customer: "Walmart" }], ending: 350 },
        { production: 800, productionDetails: [{ ref: "WO-00108", qty: 800, line: "P2" }], shipments: 620, shipmentDetails: [{ ref: "SO-50254", qty: 420, customer: "Costco" }, { ref: "SO-50255", qty: 200, customer: "Target" }], ending: 530 },
        { production: 0, productionDetails: [], shipments: 580, shipmentDetails: [{ ref: "SO-50256", qty: 380, customer: "Costco" }, { ref: "SO-50257", qty: 200, customer: "Safeway" }], ending: -50, hasIntraWeekNegative: true },
        { production: 1000, productionDetails: [{ ref: "WO-00109", qty: 1000, line: "P1" }], shipments: 600, shipmentDetails: [{ ref: "SO-50258", qty: 400, customer: "Costco" }, { ref: "SO-50259", qty: 200, customer: "Target" }], ending: 350 }
      ]
    },
    {
      id: 2,
      sku: "EVOL-293",
      name: "Steak Burrito",
      category: "Entree Burritos",
      avgWeeklyShipments: 1200,
      beginningInventory: 8500,
      weeks: [1000, 1000, 1000, 0, 1200, 0, 1200, 0, 1500, 0, 1500, 0, 1500].map((prod, i) => ({
        production: prod,
        productionDetails: prod > 0 ? [{ ref: `WO-002${10 + i}`, qty: prod, line: i % 2 === 0 ? "P1" : "P2" }] : [],
        shipments: [1200, 1150, 1250, 1180, 1200, 1150, 1250, 1180, 1200, 1150, 1250, 1180, 1200][i],
        shipmentDetails: [{ ref: `SO-502${60 + i}`, qty: [1200, 1150, 1250, 1180, 1200, 1150, 1250, 1180, 1200, 1150, 1250, 1180, 1200][i], customer: "Costco" }],
        ending: [8300, 8150, 7900, 6720, 6720, 5570, 5520, 4340, 4640, 3490, 3740, 2560, 2860][i]
      }))
    },
    {
      id: 3,
      sku: "MCF-294",
      name: "Sausage Egg and Cheese Burrito",
      category: "Breakfast Burritos",
      avgWeeklyShipments: 845,
      beginningInventory: 5200,
      weeks: [800, 800, 800, 0, 900, 0, 900, 0, 1000, 0, 1000, 0, 1000].map((prod, i) => ({
        production: prod,
        productionDetails: prod > 0 ? [{ ref: `WO-003${10 + i}`, qty: prod, line: "P2" }] : [],
        shipments: [850, 820, 880, 840, 850, 820, 880, 840, 850, 820, 880, 840, 850][i],
        shipmentDetails: [{ ref: `SO-503${10 + i}`, qty: [850, 820, 880, 840, 850, 820, 880, 840, 850, 820, 880, 840, 850][i], customer: "Target" }],
        ending: [5150, 5130, 5050, 4210, 4260, 3440, 3460, 2620, 2770, 1950, 2070, 1230, 1380][i]
      }))
    },
    {
      id: 4,
      sku: "MCF-296",
      name: "Bacon Egg and Cheese Burrito",
      category: "Breakfast Burritos",
      avgWeeklyShipments: 695,
      beginningInventory: 3800,
      weeks: [600, 600, 600, 0, 700, 0, 700, 0, 800, 0, 800, 0, 1000].map((prod, i) => ({
        production: prod,
        productionDetails: prod > 0 ? [{ ref: `WO-004${10 + i}`, qty: prod, line: "P3" }] : [],
        shipments: [700, 680, 720, 690, 700, 680, 720, 690, 700, 680, 720, 690, 700][i],
        shipmentDetails: [{ ref: `SO-504${10 + i}`, qty: [700, 680, 720, 690, 700, 680, 720, 690, 700, 680, 720, 690, 700][i], customer: "Walmart" }],
        ending: [3700, 3620, 3500, 2810, 2810, 2130, 2110, 1420, 1520, 840, 920, 230, 530][i]
      }))
    },
    {
      id: 5,
      sku: "DH-B04-CHILI",
      name: "Daily Harvest Bowl - Chili",
      category: "Ready Meals",
      avgWeeklyShipments: 1295,
      beginningInventory: 6400,
      weeks: [1200, 1200, 1200, 0, 1500, 0, 1500, 0, 1800, 0, 1800, 0, 2000].map((prod, i) => ({
        production: prod,
        productionDetails: prod > 0 ? [{ ref: `WO-005${10 + i}`, qty: prod, line: "P1" }] : [],
        shipments: [1300, 1250, 1350, 1280, 1300, 1250, 1350, 1280, 1300, 1250, 1350, 1280, 1300][i],
        shipmentDetails: [{ ref: `SO-505${10 + i}`, qty: [1300, 1250, 1350, 1280, 1300, 1250, 1350, 1280, 1300, 1250, 1350, 1280, 1300][i], customer: "Daily Harvest" }],
        ending: [6300, 6250, 6100, 4820, 5020, 3770, 3920, 2640, 3140, 1890, 2340, 1060, 1760][i]
      }))
    },
    {
      id: 6,
      sku: "DH-D04-FALL",
      name: "Daily Harvest Bowl - Fall Harvest",
      category: "Ready Meals",
      avgWeeklyShipments: 1405,
      beginningInventory: 7200,
      weeks: [1500, 1500, 1500, 0, 1800, 0, 1800, 0, 2000, 0, 2000, 0, 2000].map((prod, i) => ({
        production: prod,
        productionDetails: prod > 0 ? [{ ref: `WO-006${10 + i}`, qty: prod, line: "P2" }] : [],
        shipments: [1400, 1350, 1450, 1380, 1400, 1350, 1450, 1380, 1400, 1350, 1450, 1380, 1400][i],
        shipmentDetails: [{ ref: `SO-506${10 + i}`, qty: [1400, 1350, 1450, 1380, 1400, 1350, 1450, 1380, 1400, 1350, 1450, 1380, 1400][i], customer: "Daily Harvest" }],
        ending: [7300, 7450, 7500, 6120, 6520, 5170, 5520, 4140, 4740, 3390, 3940, 2560, 3160][i]
      }))
    }
  ];

  // Get unique categories
  const categories = [...new Set(finishedGoodsData.map(p => p.category))];
  
  // Stock status options
  const stockStatuses = [
    { id: 'critical', label: 'Critical (< 1 week)', color: 'text-red-700' },
    { id: 'low', label: 'Low (1-3 weeks)', color: 'text-yellow-700' },
    { id: 'good', label: 'Good (3-5 weeks)', color: 'text-green-700' },
    { id: 'overstocked', label: 'Overstocked (> 5 weeks)', color: 'text-blue-700' },
    { id: 'intraweek', label: 'Intra-week Negative', color: 'text-orange-700' }
  ];

  // Helper function to determine stock status
  const getStockStatus = (product) => {
    const hasIntraWeekNegative = product.weeks.some(w => w.hasIntraWeekNegative);
    if (hasIntraWeekNegative) return 'intraweek';
    
    const avgWeeksOnHand = product.weeks.reduce((sum, week) => {
      return sum + calculateWeeksOnHand(week.ending, product.avgWeeklyShipments);
    }, 0) / product.weeks.length;
    
    if (avgWeeksOnHand < 1) return 'critical';
    if (avgWeeksOnHand < 3) return 'low';
    if (avgWeeksOnHand <= 5) return 'good';
    return 'overstocked';
  };

  // Filter products
  const filteredProducts = finishedGoodsData.filter(product => {
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSKU = product.sku.toLowerCase().includes(search);
      const matchesName = product.name.toLowerCase().includes(search);
      if (!matchesSKU && !matchesName) return false;
    }
    
    // Category filter
    if (selectedCategories.length > 0) {
      if (!selectedCategories.includes(product.category)) return false;
    }
    
    // Stock status filter
    if (selectedStockStatus.length > 0) {
      const status = getStockStatus(product);
      if (!selectedStockStatus.includes(status)) return false;
    }
    
    return true;
  });

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCategories([]);
    setSelectedStockStatus([]);
  };

  // Toggle category selection
  const toggleCategory = (category) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  // Toggle stock status selection
  const toggleStockStatus = (status) => {
    setSelectedStockStatus(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const getCellColor = (weeksOnHand, hasIntraWeekNegative = false) => {
    if (hasIntraWeekNegative) return "bg-orange-50 text-orange-900 border-2 border-orange-400"; // Warning for intra-week negative
    if (weeksOnHand < 1) return "bg-red-50 text-red-900";
    if (weeksOnHand < 3) return "bg-yellow-50 text-yellow-900";
    if (weeksOnHand <= 5) return "bg-green-50 text-green-900";
    return "bg-blue-50 text-blue-900"; // Overstocked
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Planning - Finished Goods</h1>
        <p className="text-sm text-gray-600 flex items-center gap-2">
          <Info className="w-4 h-4" />
          Projected inventory: Beginning + Planned Production (Work Orders) - Planned Shipments (NetSuite Sales Orders)
        </p>
      </div>

      {/* Compact Filter Bar */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            {/* Search Bar */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by SKU or Product Name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Filters Button */}
            <button
              onClick={() => setShowFilterModal(true)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              <Filter className="w-4 h-4" />
              Filters
              {(selectedCategories.length > 0 || selectedStockStatus.length > 0) && (
                <span className="ml-1 px-2 py-0.5 text-xs bg-blue-500 text-white rounded-full">
                  {selectedCategories.length + selectedStockStatus.length}
                </span>
              )}
            </button>

            {/* Clear Filters Button */}
            {(searchTerm || selectedCategories.length > 0 || selectedStockStatus.length > 0) && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                <X className="w-4 h-4" />
                Clear
              </button>
            )}
          </div>

          {/* Active Filter Badges */}
          {(selectedCategories.length > 0 || selectedStockStatus.length > 0) && (
            <div className="flex flex-wrap gap-2 mt-3">
              {selectedCategories.map(cat => (
                <span key={cat} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                  {cat}
                  <button onClick={() => toggleCategory(cat)} className="hover:text-blue-900">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {selectedStockStatus.map(status => {
                const statusObj = stockStatuses.find(s => s.id === status);
                return (
                  <span key={status} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-800 text-sm rounded-full">
                    {statusObj?.label}
                    <button onClick={() => toggleStockStatus(status)} className="hover:text-gray-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          {/* Filter Summary */}
          <div className="mt-3 text-sm text-gray-600">
            Showing {filteredProducts.length} of {finishedGoodsData.length} products
          </div>
        </CardContent>
      </Card>

      {/* Filter Modal */}
      <Dialog open={showFilterModal} onOpenChange={setShowFilterModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Filter Products</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Category</label>
              <div className="flex flex-wrap gap-2">
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => toggleCategory(category)}
                    className={`px-4 py-2 text-sm rounded-lg border transition-colors ${
                      selectedCategories.includes(category)
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-500'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Stock Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Stock Status</label>
              <div className="space-y-2">
                {stockStatuses.map(status => (
                  <label key={status.id} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={selectedStockStatus.includes(status.id)}
                      onChange={() => toggleStockStatus(status.id)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className={`text-sm font-medium ${status.color}`}>{status.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={() => {
                clearFilters();
                setShowFilterModal(false);
              }}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
            >
              Clear All
            </button>
            <button
              onClick={() => setShowFilterModal(false)}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <CardTitle>Finished Goods - 13 Week Projection</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-100 sticky top-0 z-10">
                <tr>
                  <th className="sticky z-20 bg-gray-100 text-left py-2 px-1 font-semibold text-gray-700" style={{left: '0px', minWidth: '30px', width: '30px'}}></th>
                  <th className="sticky z-20 bg-gray-100 text-left py-2 px-2 font-semibold text-gray-700 border-r" style={{left: '30px', minWidth: '120px', width: '120px'}}>SKU</th>
                  <th className="sticky z-20 bg-gray-100 text-left py-2 px-2 font-semibold text-gray-700 border-r" style={{left: '150px', minWidth: '200px', width: '200px'}}>Product Name</th>
                  <th className="sticky z-20 bg-gray-100 text-center py-2 px-2 font-semibold text-gray-700 border-r bg-blue-50" style={{left: '350px', minWidth: '80px', width: '80px'}}>Begin</th>
                  {weekHeaders.map((weekDate, idx) => (
                    <th key={idx} className="text-center py-3 px-1 font-semibold text-gray-700 border-r" style={{minWidth: '100px'}}>
                      {weekDate}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product, idx) => (
                  <>
                    <tr 
                      key={product.id} 
                      className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} cursor-pointer hover:bg-blue-50`}
                      onClick={() => openDetailModal(product)}
                    >
                      <td className="sticky z-10 bg-inherit py-1 px-1" style={{left: '0px'}}>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRow(product.id);
                          }}
                          className="hover:bg-gray-200 rounded p-0.5"
                        >
                          {expandedRows[product.id] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        </button>
                      </td>
                      <td className="sticky z-10 bg-inherit py-1 px-2 text-sm font-medium text-gray-900 border-r" style={{left: '30px'}}>{product.sku}</td>
                      <td className="sticky z-10 bg-inherit py-1 px-2 text-sm text-gray-700 border-r" style={{left: '150px'}}>{product.name}</td>
                      <td className="sticky z-10 bg-inherit py-1 px-2 text-center font-semibold text-gray-900 border-r bg-blue-50" style={{left: '350px'}}>
                        {product.beginningInventory.toLocaleString()} <span className="text-xs text-gray-500">CS</span>
                      </td>
                      {product.weeks.map((week, weekIdx) => {
                        const weeksOnHand = calculateWeeksOnHand(week.ending, product.avgWeeklyShipments);
                        return (
                          <td key={weekIdx} className="py-1 px-1 border-r text-center">
                            <div className={`font-semibold text-xs px-2 py-1 rounded inline-block ${getCellColor(weeksOnHand, week.hasIntraWeekNegative)}`}>
                              {week.ending.toLocaleString()}
                              <div className="text-[10px] text-gray-600 mt-0.5">{weeksOnHand.toFixed(1)}w</div>
                              {week.hasIntraWeekNegative && <div className="text-[9px] text-orange-700 font-bold mt-0.5">⚠</div>}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                    {expandedRows[product.id] && (
                      <tr className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="sticky z-10 bg-inherit" style={{left: '0px'}}></td>
                        <td className="sticky z-10 bg-inherit py-1 px-2 text-xs text-gray-600" style={{left: '30px'}} colSpan="3">
                          <div className="space-y-1">
                            <div className="text-green-700 font-medium">+ Planned Production</div>
                            <div className="text-red-700 font-medium">- Planned Shipments</div>
                          </div>
                        </td>
                        {product.weeks.map((week, weekIdx) => (
                          <td key={weekIdx} className="py-1 px-1 border-r">
                            <div className="text-xs space-y-1 relative">
                              <div 
                                className="text-green-700 group relative inline-block w-full"
                              >
                                <span className="cursor-help">+{week.production}</span>
                                {week.production > 0 && week.productionDetails.length > 0 && (
                                  <div className="hidden group-hover:block absolute z-50 bg-gray-900 text-white text-xs rounded p-2 shadow-lg left-0 top-6 w-48">
                                    {week.productionDetails.map((wo, i) => (
                                      <div key={i} className="mb-1">
                                        <strong>{wo.ref}</strong><br />
                                        {wo.qty} CS on Line {wo.line}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div 
                                className="text-red-700 group relative inline-block w-full"
                              >
                                <span className="cursor-help">-{week.shipments}</span>
                                {week.shipmentDetails.length > 0 && (
                                  <div className="hidden group-hover:block absolute z-50 bg-gray-900 text-white text-xs rounded p-2 shadow-lg left-0 top-6 w-48">
                                    {week.shipmentDetails.map((so, i) => (
                                      <div key={i} className="mb-1">
                                        <strong>{so.ref}</strong><br />
                                        {so.qty} CS to {so.customer}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        ))}
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="mt-4 flex items-center gap-4 text-xs text-gray-600">
        <div className="flex items-center gap-2">
          <div className="w-12 h-6 bg-blue-50 border border-blue-200 rounded"></div>
          <span>Overstocked (5+ weeks)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-12 h-6 bg-green-50 border border-green-200 rounded"></div>
          <span>Good Stock (3-5 weeks)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-12 h-6 bg-yellow-50 border border-yellow-200 rounded"></div>
          <span>Low Stock (1-3 weeks)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-12 h-6 bg-red-50 border border-red-200 rounded"></div>
          <span>Critical (&lt;1 week)</span>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedProduct && (
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl">{selectedProduct.sku} - {selectedProduct.name}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Inventory Chart with Weeks on Hand */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Inventory Position & Weeks on Hand</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={selectedProduct.weeks.map((w, idx) => ({ 
                      week: `W${idx + 1}`, 
                      inventory: w.ending,
                      weeksOnHand: calculateWeeksOnHand(w.ending, selectedProduct.avgWeeklyShipments)
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" />
                      <YAxis yAxisId="left" label={{ value: 'Inventory (CS)', angle: -90, position: 'insideLeft' }} />
                      <YAxis yAxisId="right" orientation="right" label={{ value: 'Weeks on Hand', angle: 90, position: 'insideRight' }} />
                      <Tooltip />
                      <Line yAxisId="left" type="monotone" dataKey="inventory" stroke="#3b82f6" strokeWidth={2} name="Inventory" />
                      <Line yAxisId="right" type="monotone" dataKey="weeksOnHand" stroke="#10b981" strokeWidth={2} name="Weeks on Hand" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Detailed Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Weekly Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {selectedProduct.weeks.map((week, idx) => {
                      const weeksOnHand = calculateWeeksOnHand(week.ending, selectedProduct.avgWeeklyShipments);
                      
                      // Calculate day-by-day breakdown
                      const startOfWeek = new Date(2025, 9, 19 + (idx * 7)); // Starting from Oct 19, 2025
                      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                      const dayByDay = [];
                      
                      let runningBalance = idx === 0 ? selectedProduct.beginningInventory : selectedProduct.weeks[idx - 1].ending;
                      
                      // Create transactions with dates (mock dates based on day of week)
                      const transactions = [];
                      week.productionDetails.forEach((wo, i) => {
                        const date = new Date(startOfWeek);
                        date.setDate(startOfWeek.getDate() + (i * 2)); // Spread production across week
                        transactions.push({ ...wo, type: 'production', date });
                      });
                      week.shipmentDetails.forEach((so, i) => {
                        const date = new Date(startOfWeek);
                        date.setDate(startOfWeek.getDate() + (i * 2 + 1)); // Spread shipments across week
                        transactions.push({ ...so, type: 'shipment', date });
                      });
                      transactions.sort((a, b) => a.date - b.date);
                      
                      // Generate day-by-day view
                      for (let d = 0; d < 7; d++) {
                        const currentDate = new Date(startOfWeek);
                        currentDate.setDate(startOfWeek.getDate() + d);
                        const dayTransactions = transactions.filter(t => t.date.toDateString() === currentDate.toDateString());
                        
                        dayByDay.push({
                          day: days[d],
                          date: currentDate,
                          transactions: dayTransactions,
                          startBalance: runningBalance
                        });
                        
                        // Update running balance for this day
                        dayTransactions.forEach(t => {
                          if (t.type === 'production') runningBalance += t.qty;
                          else runningBalance -= t.qty;
                        });
                      }
                      
                      return (
                        <div key={idx} className="border rounded-lg p-4 bg-gray-50">
                          <div className="grid grid-cols-3 gap-4 mb-4">
                            <div>
                              <h4 className="font-semibold text-gray-900">Week {idx + 1}</h4>
                              <p className="text-xs text-gray-500">Day-by-Day Breakdown</p>
                            </div>
                            <div className="text-center">
                              <div className={`px-3 py-1 rounded font-semibold text-sm ${getCellColor(weeksOnHand)}`}>
                                {week.ending.toLocaleString()} CS
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-semibold text-gray-700">
                                {weeksOnHand.toFixed(1)} Weeks on Hand
                              </div>
                              <div className="text-xs text-gray-500">
                                Avg: {selectedProduct.avgWeeklyShipments} CS/wk
                              </div>
                            </div>
                          </div>
                          
                          {/* Day-by-Day Table */}
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs border">
                              <thead className="bg-gray-200">
                                <tr>
                                  <th className="border px-2 py-1 text-left">Day</th>
                                  <th className="border px-2 py-1 text-left">Transaction</th>
                                  <th className="border px-2 py-1 text-right">Amount</th>
                                  <th className="border px-2 py-1 text-right">Balance</th>
                                </tr>
                              </thead>
                              <tbody>
                                {dayByDay.map((day, dayIdx) => {
                                  let dayBalance = day.startBalance;
                                  const rows = [];
                                  
                                  if (day.transactions.length === 0) {
                                    rows.push(
                                      <tr key={`${dayIdx}-empty`} className="bg-white">
                                        <td className="border px-2 py-1 font-medium">{day.day} {day.date.getMonth()+1}/{day.date.getDate()}</td>
                                        <td className="border px-2 py-1 text-gray-400 italic" colSpan="2">No activity</td>
                                        <td className="border px-2 py-1 text-right font-medium">{dayBalance.toLocaleString()}</td>
                                      </tr>
                                    );
                                  } else {
                                    day.transactions.forEach((trans, transIdx) => {
                                      const isProduction = trans.type === 'production';
                                      const amount = isProduction ? trans.qty : -trans.qty;
                                      dayBalance += amount;
                                      
                                      rows.push(
                                        <tr key={`${dayIdx}-${transIdx}`} className={dayBalance < 0 ? 'bg-red-100' : 'bg-white'}>
                                          {transIdx === 0 && (
                                            <td className="border px-2 py-1 font-medium" rowSpan={day.transactions.length}>
                                              {day.day} {day.date.getMonth()+1}/{day.date.getDate()}
                                            </td>
                                          )}
                                          <td className={`border px-2 py-1 ${isProduction ? 'text-green-700' : 'text-red-700'}`}>
                                            <a href="#" className="text-blue-600 hover:underline font-medium">{trans.ref}</a>
                                            <span className="text-gray-600 ml-1">- {isProduction ? `Line ${trans.line}` : trans.customer}</span>
                                          </td>
                                          <td className={`border px-2 py-1 text-right font-medium ${isProduction ? 'text-green-700' : 'text-red-700'}`}>
                                            {isProduction ? '+' : ''}{amount.toLocaleString()}
                                          </td>
                                          <td className={`border px-2 py-1 text-right font-bold ${dayBalance < 0 ? 'text-red-700' : 'text-gray-900'}`}>
                                            {dayBalance.toLocaleString()}
                                          </td>
                                        </tr>
                                      );
                                    });
                                  }
                                  
                                  return <Fragment key={`day-${dayIdx}`}>{rows}</Fragment>;
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default PlanningFinishedGoodsPage;
